import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Brand } from "./Brand";
import { Button } from "./ui/button";
import Stepper from "./Stepper";
import QuoteScreen from "./QuoteScreen";
import WalletScreen from "./WalletScreen";
import KycScreen from "./KycScreen";
import SepaInstructions from "./SepaInstructions";
import { useAuth } from "../lib/auth";
import api, { formatEUR } from "../lib/api";
import { toast } from "sonner";
import PriceTicker from "./PriceTicker";
import { motion, AnimatePresence } from "framer-motion";
import { UserCircle, LogOut, Sparkles } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { useWidgetParams } from "../lib/widgetParams";
import TrustStrip from "./TrustStrip";
import NetworkConfirmDialog from "./NetworkConfirmDialog";
import InstaxIframe from "./InstaxIframe";

const STEPS = ["Quote", "Wallet", "Verify", "Pay"];

export default function Widget({ embedded = false, initialAsset, initialAmount }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const params = useWidgetParams();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});  // { quote, payment, wallet }
  const [order, setOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // 🔒 Network-confirm modal — last-chance guardrail before any payment redirect.
  const [pendingPayload, setPendingPayload] = useState(null);

  const effectiveAsset = initialAsset || params.asset;
  const effectiveAmount = initialAmount || params.amount;

  const handleQuoteContinue = ({ quote, payment }) => {
    setData({ ...data, quote, payment });
    setStep(1);
  };

  const handleWalletContinue = (address) => {
    const next = { ...data, wallet: address };
    setData(next);
    // 🔒 Always go through the network-confirm modal next, regardless of
    // KYC state. KYC verification has its own dedicated step; the network
    // confirm is the LAST guardrail before money moves, so it always fires.
    if (!user?.kyc_tier || user.kyc_tier < 1) {
      setStep(2);
    } else {
      setPendingPayload(next);
    }
  };

  const handleKycComplete = () => {
    setPendingPayload(data);
  };

  const handleConfirmedProceed = () => {
    const payload = pendingPayload;
    setPendingPayload(null);
    handleCreateOrder(payload);
  };

  const handleCancelConfirm = () => {
    setPendingPayload(null);
  };

  const handleCreateOrder = async (d) => {
    setSubmitting(true);
    try {
      const r = await api.post("/v1/orders", {
        quote_id: d.quote.quote_id,
        wallet_address: d.wallet,
        payment_method: d.payment,
        partner_id: params.partnerId || undefined,
        partner_user_id: params.userId || undefined,
      });
      setOrder(r.data);
      setStep(3);
      // 🩹 Iter49 — InstaXchange now ONLY supports iframe-embed mode (per
      // their docs since Sep 2025). The previous same-tab redirect hit the
      // legacy widget code path on their side which silently fails on
      // `/payment_session/<id>/init` → forever-spinner / never redirects to
      // Banxa. The iframe is rendered inline in step 3 below.
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Order failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className={embedded ? "" : "xch-app-bg"}>
      {!embedded && (
        <header className="xch-ink sticky top-0 z-20">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <button onClick={() => { setStep(0); setData({}); setOrder(null); }} data-testid="header-brand-btn">
              {params.logo ? (
                <img src={params.logo} alt="Partner logo" className="h-8 max-w-[180px] object-contain" />
              ) : (
                <Brand size={28} theme="dark" />
              )}
            </button>
            <div className="flex items-center gap-2">
              {user ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2 text-white hover:text-white hover:bg-white/10" data-testid="user-menu-btn">
                      <UserCircle className="w-4 h-4" /> {user.name?.split(" ")[0] || "Account"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-56 rounded-xl">
                    <div className="px-2 py-1.5 border-b border-slate-100 mb-1.5">
                      <div className="font-display font-semibold text-sm text-foreground">{user.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    </div>
                    <button onClick={() => navigate("/account")} className="w-full text-left text-sm px-2 py-1.5 hover:bg-muted rounded-md transition-colors" data-testid="menu-account-btn">My orders</button>
                    <button onClick={() => navigate("/partner/embed")} className="w-full text-left text-sm px-2 py-1.5 hover:bg-muted rounded-md transition-colors" data-testid="menu-embed-btn">Partner embed</button>
                    <button onClick={logout} className="w-full text-left text-sm px-2 py-1.5 hover:bg-muted rounded-md text-destructive flex items-center gap-1.5" data-testid="menu-logout-btn"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
                  </PopoverContent>
                </Popover>
              ) : (
                <button onClick={() => navigate("/partner/embed")} className="text-sm font-semibold text-white/80 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/10 transition-colors hidden sm:inline-flex items-center" data-testid="header-partners-btn">For partners →</button>
              )}
            </div>
          </div>
          <PriceTicker />
        </header>
      )}

      <main className={embedded ? "px-4 py-6" : "px-4 py-8 md:py-12"}>
        <div className="max-w-md mx-auto">
          {!embedded && (
            <div className="text-center mb-7">
              <div className="xch-eyebrow mb-3">
                <Sparkles className="w-3 h-3" /> EU's transparent on-ramp
              </div>
              <h1 className="xch-headline text-[2.4rem] sm:text-[3rem]">
                Buy crypto.<br />Skip the hidden fees.
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 max-w-sm mx-auto">
                Direct to your wallet. SEPA or card. One rate, no surprises.
              </p>
            </div>
          )}

          {!embedded && <TrustStrip />}

          <div className="xch-card p-5 md:p-7" data-testid="widget-container">
            <Stepper steps={STEPS} current={step} />

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
              >
                {step === 0 && <QuoteScreen onContinue={handleQuoteContinue} initialAsset={effectiveAsset} initialAmount={effectiveAmount} />}
                {step === 1 && (
                  <WalletScreen quote={data.quote} payment={data.payment}
                    onBack={() => setStep(0)} onContinue={handleWalletContinue}
                    lockedAddress={params.wallet} />
                )}
                {step === 2 && <KycScreen onComplete={handleKycComplete} />}
                {step === 3 && order && data.payment === "sepa" && (
                  <SepaInstructions order={order} onContinue={() => navigate(`/widget/status/${order.order_id}`)} />
                )}
                {step === 3 && order && data.payment === "card" && order.checkout_url && (
                  <div data-testid="instax-checkout-host" className="-mx-5 md:-mx-7 -mb-5 md:-mb-7">
                    <InstaxIframe url={order.checkout_url} testid="instax-checkout-iframe" />
                  </div>
                )}
                {step === 3 && submitting && (
                  <div className="text-center py-10 text-sm text-slate-500">Creating your order…</div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="text-center text-[11px] text-muted-foreground mt-5 leading-relaxed">
            Crypto purchases are non-refundable.
          </div>
        </div>
      </main>

      {/* 🔒 Last-chance guardrail before money moves */}
      <NetworkConfirmDialog
        open={!!pendingPayload}
        onConfirm={handleConfirmedProceed}
        onCancel={handleCancelConfirm}
        asset={data.quote?.asset}
        network={data.quote?.network}
        receiveAmount={data.quote?.receive_amount}
        walletAddress={data.wallet}
        fiatAmount={data.quote?.fiat_amount}
        fiatCurrency={data.quote?.fiat_currency || "EUR"}
        paymentMethod={data.payment}
      />
    </div>
  );
}
